import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Upload, Download, FileSpreadsheet, CheckCircle, AlertTriangle, Info } from 'lucide-react';
import { API_URL, ENTITY_CONFIG } from '../utils/constants';
import { generateTemplate, parseExcelFile, getUniqueKey } from '../utils/excelUtils';

const BulkUploadModal = ({ isOpen, onClose, entityType, companies, existingEntities, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [errors, setErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [duplicatesInFile, setDuplicatesInFile] = useState(0);

  const config = ENTITY_CONFIG[entityType];
  
  // Early return después de los hooks
  if (!config) return null;

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.match(/\.(xlsx|xls)$/i)) {
      toast.error('Solo se permiten archivos Excel (.xlsx, .xls)');
      return;
    }

    setFile(selectedFile);
    setUploadResult(null);

    try {
      const result = await parseExcelFile(selectedFile, entityType);
      setParsedData(result.records);
      setErrors(result.errors);
      setDuplicatesInFile(result.duplicatesInFile || 0);

      if (result.duplicatesInFile > 0) {
        toast.warning(`${result.duplicatesInFile} registros duplicados detectados en el archivo`);
      }
      if (result.errors.length > 0) {
        toast.warning(`${result.errors.length} filas con errores`);
      } else if (result.records.length > 0) {
        toast.success(`${result.records.length} registros listos para cargar`);
      }
    } catch (error) {
      toast.error(error.message);
      setFile(null);
      setParsedData(null);
    }
  };

  const handleUpload = async () => {
    if (!parsedData || parsedData.length === 0) {
      toast.error('No hay datos para cargar');
      return;
    }

    setUploading(true);
    const results = { 
      success: 0, 
      failed: 0, 
      skippedDuplicate: 0,
      skippedExists: 0,
      errors: [] 
    };
    
    const uniqueKey = getUniqueKey(entityType);

    try {
      for (const record of parsedData) {
        try {
          // Para entidades que necesitan company_id, convertir company_cuit a company_id
          let dataToSend = { ...record };
          
          if (record.company_cuit && entityType !== 'companies') {
            const company = companies.find(c => c.cuit_rut === record.company_cuit);
            if (!company) {
              results.failed++;
              results.errors.push({ data: record, error: `Empresa con CUIT ${record.company_cuit} no encontrada` });
              continue;
            }
            dataToSend.company_id = company.id;
            delete dataToSend.company_cuit;
          }

          // Para empresas, agregar transportista_id vacío si no existe
          if (entityType === 'companies' && !dataToSend.transportista_id) {
            dataToSend.transportista_id = '';
          }

          // Verificar si ya existe en la base de datos
          const keyValue = entityType === 'companies' ? record.cuit_rut : 
                           entityType === 'drivers' ? record.dni : record.license_plate;
          
          const existsInDb = existingEntities?.some(e => {
            if (entityType === 'companies') return e.cuit_rut === keyValue;
            if (entityType === 'drivers') return e.dni === keyValue;
            return e.license_plate === keyValue;
          });

          if (existsInDb) {
            results.skippedExists++;
            continue; // Saltar sin agregar a errores
          }

          await axios.post(`${API_URL}/${config.endpoint}`, dataToSend);
          results.success++;
        } catch (error) {
          const errorMsg = error.response?.data?.detail || 'Error desconocido';
          // Si el error es de duplicado, contar como skipped
          if (errorMsg.toLowerCase().includes('duplicad') || 
              errorMsg.toLowerCase().includes('ya existe') ||
              errorMsg.toLowerCase().includes('already exists')) {
            results.skippedExists++;
          } else {
            results.failed++;
            results.errors.push({ 
              data: record, 
              error: errorMsg 
            });
          }
        }
      }

      // Cerrar modal y mostrar resultado
      onSuccess();
      handleClose();
      
      // Mostrar popup con resultado detallado
      const totalProcessed = results.success + results.skippedExists + results.failed;
      const entityLabel = config.label.toLowerCase();
      
      if (results.failed === 0) {
        // Todo salió bien
        toast.success(
          <div className="space-y-1">
            <p className="font-semibold">Carga de {entityLabel} completada</p>
            <div className="text-sm">
              <p>✓ {results.success} creados</p>
              {results.skippedExists > 0 && <p>⊘ {results.skippedExists} ya existían</p>}
              <p className="text-xs text-slate-500 mt-1">Total procesados: {totalProcessed}</p>
            </div>
          </div>,
          { duration: 5000 }
        );
      } else {
        // Hubo algunos errores
        toast.warning(
          <div className="space-y-1">
            <p className="font-semibold">Carga de {entityLabel} con errores</p>
            <div className="text-sm">
              <p>✓ {results.success} creados</p>
              {results.skippedExists > 0 && <p>⊘ {results.skippedExists} ya existían</p>}
              <p>✗ {results.failed} fallidos</p>
              <p className="text-xs text-slate-500 mt-1">Total procesados: {totalProcessed}</p>
            </div>
          </div>,
          { duration: 7000 }
        );
      }
      
      return; // Exit early since we already closed
    } catch (error) {
      toast.error('Error en la carga masiva');
    } finally {
      setUploading(false);
    }
  };

  const resetModal = () => {
    setFile(null);
    setParsedData(null);
    setErrors([]);
    setUploadResult(null);
    setDuplicatesInFile(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const uniqueKeyLabel = {
    companies: 'CUIT/RUT',
    trucks: 'Patente',
    trailers: 'Patente',
    drivers: 'DNI'
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload size={20} />
            Carga Masiva de {config.label}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Info de llave única */}
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200 flex items-start gap-2">
            <Info size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-700">
              <strong>Llave única:</strong> {uniqueKeyLabel[entityType]}. 
              Los registros duplicados en el archivo o que ya existan en la base de datos serán omitidos automáticamente.
            </div>
          </div>

          {/* Paso 1: Descargar plantilla */}
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <h3 className="font-medium text-slate-700 mb-2">Paso 1: Descargar plantilla</h3>
            <p className="text-sm text-slate-500 mb-3">
              Descarga la plantilla Excel con los campos requeridos para {config.label.toLowerCase()}.
            </p>
            <Button 
              onClick={() => generateTemplate(entityType)} 
              variant="outline" 
              size="sm"
              className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
            >
              <Download size={16} className="mr-2" />
              Descargar Plantilla
            </Button>
          </div>

          {/* Paso 2: Subir archivo */}
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <h3 className="font-medium text-slate-700 mb-2">Paso 2: Subir archivo Excel</h3>
            <p className="text-sm text-slate-500 mb-3">
              Completa la plantilla con los datos y súbela aquí.
            </p>
            
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
              id="bulk-file-input"
            />
            
            <div className="flex items-center gap-3">
              <Button 
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                size="sm"
              >
                <FileSpreadsheet size={16} className="mr-2" />
                Seleccionar Archivo
              </Button>
              {file && (
                <span className="text-sm text-slate-600">
                  {file.name}
                </span>
              )}
            </div>
          </div>

          {/* Duplicados en archivo */}
          {duplicatesInFile > 0 && (
            <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
              <div className="flex items-center gap-2">
                <AlertTriangle size={16} className="text-amber-600" />
                <span className="text-sm text-amber-700">
                  <strong>{duplicatesInFile}</strong> registros duplicados detectados en el archivo (se omitirán)
                </span>
              </div>
            </div>
          )}

          {/* Vista previa de datos */}
          {parsedData && parsedData.length > 0 && (
            <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle size={18} className="text-emerald-600" />
                <h3 className="font-medium text-emerald-700">
                  {parsedData.length} registros listos para cargar
                </h3>
              </div>
              <div className="max-h-40 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-emerald-100">
                      {config.fields.slice(0, 4).map(f => (
                        <th key={f.key} className="px-2 py-1 text-left text-emerald-700">{f.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.slice(0, 5).map((row, idx) => (
                      <tr key={idx} className="border-t border-emerald-200">
                        {config.fields.slice(0, 4).map(f => (
                          <td key={f.key} className="px-2 py-1 text-emerald-800">{row[f.key] || '-'}</td>
                        ))}
                      </tr>
                    ))}
                    {parsedData.length > 5 && (
                      <tr>
                        <td colSpan={4} className="px-2 py-1 text-center text-emerald-600">
                          ... y {parsedData.length - 5} más
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Errores de parseo */}
          {errors.length > 0 && (
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={18} className="text-amber-600" />
                <h3 className="font-medium text-amber-700">
                  {errors.length} filas con errores (no se cargarán)
                </h3>
              </div>
              <ul className="text-xs text-amber-700 space-y-1 max-h-24 overflow-y-auto">
                {errors.slice(0, 10).map((err, idx) => (
                  <li key={idx}>Fila {err.row}: {err.message}</li>
                ))}
                {errors.length > 10 && (
                  <li className="text-amber-600">... y {errors.length - 10} errores más</li>
                )}
              </ul>
            </div>
          )}

          {/* Resultado de carga */}
          {uploadResult && (
            <div className={`rounded-lg p-4 border ${uploadResult.failed > 0 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
              <h3 className="font-medium mb-2">Resultado de la carga:</h3>
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="text-emerald-600">✓ {uploadResult.success} creados</span>
                {uploadResult.skippedExists > 0 && (
                  <span className="text-blue-600">⊘ {uploadResult.skippedExists} ya existían</span>
                )}
                {uploadResult.failed > 0 && (
                  <span className="text-red-600">✗ {uploadResult.failed} fallidos</span>
                )}
              </div>
              {uploadResult.errors.length > 0 && (
                <div className="mt-2 max-h-32 overflow-y-auto">
                  <p className="text-xs text-red-600 font-medium mb-1">Errores:</p>
                  {uploadResult.errors.slice(0, 5).map((err, idx) => (
                    <p key={idx} className="text-xs text-red-500">
                      {err.error}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Botón de carga */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={handleClose}>Cancelar</Button>
            <Button 
              onClick={handleUpload}
              disabled={!parsedData || parsedData.length === 0 || uploading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Cargando...
                </>
              ) : (
                <>
                  <Upload size={16} className="mr-2" />
                  Cargar {parsedData?.length || 0} Registros
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkUploadModal;
